// Coalesces high-frequency work into a single flush per animation frame, so a
// burst of WebSocket events produces one Vue re-render instead of one per event.
// Queued tasks run in FIFO order, preserving event ordering. Falls back to
// setTimeout when requestAnimationFrame is unavailable (tests, SSR) or the tab
// is hidden — rAF is paused in background tabs, which would otherwise stall the
// queue and delay updates indefinitely.
const FRAME_MS = 16;
// Watchdog paired with requestAnimationFrame: browsers PAUSE pending rAF
// callbacks while a tab is backgrounded. If the tab hides after a flush is
// scheduled, the rAF never fires and the queue strands until refocus — delaying
// list updates and dropping hidden-tab audio alerts (they run with
// document.hidden already false). A timer still fires in the background
// (throttled to ~1s), so it drains the queue; whichever fires first cancels the
// other. Kept above one frame so rAF still wins in the normal foreground path.
const RAF_WATCHDOG_MS = 250;

export const createRafBatcher = () => {
  let queue = [];
  let rafHandle = null;
  let timerHandle = null;

  const cancelScheduled = () => {
    if (rafHandle !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafHandle);
    }
    if (timerHandle !== null) clearTimeout(timerHandle);
    rafHandle = null;
    timerHandle = null;
  };

  const runQueued = () => {
    cancelScheduled();
    const tasks = queue;
    queue = [];
    tasks.forEach(task => {
      try {
        task();
      } catch (error) {
        // Isolate failures: one throwing task must not drop the rest of the
        // frame's queued tasks (the queue is already cleared above).
        // eslint-disable-next-line no-console
        console.error('[rafBatcher] queued task failed', error);
      }
    });
  };

  const isScheduled = () => rafHandle !== null || timerHandle !== null;

  const canUseRaf = () =>
    typeof requestAnimationFrame === 'function' &&
    !(typeof document !== 'undefined' && document.hidden);

  const schedule = () => {
    if (isScheduled()) return;
    if (canUseRaf()) {
      rafHandle = requestAnimationFrame(runQueued);
      // Backstop the pause-in-background case (see RAF_WATCHDOG_MS).
      timerHandle = setTimeout(runQueued, RAF_WATCHDOG_MS);
    } else {
      timerHandle = setTimeout(runQueued, FRAME_MS);
    }
  };

  return {
    enqueue(task) {
      queue.push(task);
      schedule();
    },
    // Run any pending tasks now and cancel the scheduled flush. Used by tests to
    // drain the queue deterministically.
    flush() {
      const hadWork = queue.length || isScheduled();
      cancelScheduled();
      if (hadWork) runQueued();
    },
    get size() {
      return queue.length;
    },
  };
};
